import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FaUsers, FaFutbol, FaQuestion, FaTrophy, FaBolt, FaChartBar, FaBan } from 'react-icons/fa';
import api from '../../api/axios';

function StatCard({ icon: Icon, label, value, sub, color, delay = 0 }) {
  return (
    <motion.div
      className="card flex items-center gap-4"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay }}
    >
      <div
        className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
        style={{ background: `${color}20`, border: `1px solid ${color}30` }}
      >
        <Icon size={20} style={{ color }} />
      </div>
      <div>
        <div className="text-2xl font-black text-white" style={{ fontFamily: "'Rajdhani', sans-serif" }}>
          {value !== undefined && value !== null
            ? typeof value === 'number' ? value.toLocaleString('fr-FR') : value
            : '—'}
        </div>
        <div className="text-white/60 text-xs">{label}</div>
        {sub > 0 && <div className="text-qfl-green text-xs">+{sub} cette semaine</div>}
      </div>
    </motion.div>
  );
}

const DAY_FR = { Mon: 'Lun', Tue: 'Mar', Wed: 'Mer', Thu: 'Jeu', Fri: 'Ven', Sat: 'Sam', Sun: 'Dim' };

function BarChart({ data }) {
  const maxGames = Math.max(...data.map((d) => d.games), 1);
  const today = new Date();

  return (
    <div className="flex items-end justify-between gap-2 h-40 px-2">
      {data.map((d, i) => {
        const isToday = i === data.length - 1;
        const dateLabel = d.date_label || '';

        return (
          <div key={i} className="flex flex-col items-center gap-0.5 flex-1">
            {/* Nombre de parties au-dessus */}
            <span className="text-xs font-bold" style={{ color: isToday ? '#FFB81C' : 'rgba(255,255,255,0.35)', fontFamily: "'Rajdhani', sans-serif", minHeight: 16 }}>
              {d.games > 0 ? d.games : ''}
            </span>

            <div className="relative w-full flex flex-col justify-end" style={{ height: 88 }}>
              <motion.div
                className="w-full rounded-t-md"
                style={{
                  background: isToday
                    ? 'linear-gradient(to top, #FFB81C, #fde68a)'
                    : 'linear-gradient(to top, rgba(255,184,28,0.5), rgba(251,191,36,0.6))',
                  minHeight: 4,
                  boxShadow: isToday ? '0 0 8px rgba(255,184,28,0.6)' : 'none',
                }}
                initial={{ height: 0 }}
                animate={{ height: `${(d.games / maxGames) * 100}%` }}
                transition={{ duration: 0.6, delay: i * 0.08 }}
              />
            </div>

            {/* Nom du jour */}
            <span className="text-xs font-bold" style={{ color: isToday ? '#FFB81C' : 'rgba(255,255,255,0.5)', fontFamily: "'Rajdhani', sans-serif" }}>
              {DAY_FR[d.day] || d.day}
            </span>
            {/* Date DD/MM */}
            <span className="text-xs" style={{ color: isToday ? 'rgba(255,184,28,0.7)' : 'rgba(255,255,255,0.25)', fontFamily: "'Rajdhani', sans-serif", fontSize: '0.6rem' }}>
              {dateLabel}
            </span>
          </div>
        );
      })}
    </div>
  );
}

function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get('/admin/stats'),
      api.get('/admin/activity'),
    ])
      .then(([statsRes, activityRes]) => {
        setStats(statsRes.data.stats || statsRes.data);
        setActivity(activityRes.data.days || activityRes.data || []);
      })
      .catch(() => {
        setStats(null);
        setActivity([]);
      })
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-10 h-10 rounded-full border-4 border-qfl-gold border-t-transparent animate-spin" />
      </div>
    );
  }

  // Mapping des champs API → affichage
  const totalUsers       = parseInt(stats?.users?.total_users)      || 0;
  const newUsersWeek     = parseInt(stats?.users?.new_users_week)    || 0;
  const gamesWeek        = parseInt(stats?.games?.games_this_week)   || 0;
  const totalQuestions   = parseInt(stats?.questions?.total_questions) || 0;
  const premiumUsers     = parseInt(stats?.users?.premium_users)     || 0;
  const bannedUsers      = parseInt(stats?.users?.banned_users)      || 0;
  const activeTournaments = parseInt(stats?.tournaments?.active)     || 0;

  const hasActivity = activity.length > 0;

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="text-2xl font-black text-white uppercase tracking-widest" style={{ fontFamily: "'Rajdhani', sans-serif" }}>
          Tableau de bord
        </h1>
        <p className="text-white/40 text-sm">Vue d'ensemble de OM QUIZ</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <StatCard icon={FaUsers}    label="Utilisateurs totaux"  value={totalUsers}        sub={newUsersWeek}  color="#3b82f6" delay={0}    />
        <StatCard icon={FaFutbol}   label="Parties cette semaine" value={gamesWeek}                             color="##003DA5" delay={0.05} />
        <StatCard icon={FaQuestion} label="Questions en base"     value={totalQuestions}                        color="#FFB81C" delay={0.1}  />
        <StatCard icon={FaTrophy}   label="Utilisateurs Premium"  value={premiumUsers}                          color="#8b5cf6" delay={0.15} />
        <StatCard icon={FaBolt}     label="Tournois actifs"        value={activeTournaments}                     color="#f97316" delay={0.2}  />
        <StatCard icon={FaBan}      label="Comptes bannis"         value={bannedUsers}                           color="#ef4444" delay={0.25} />
      </div>

      {/* Graphique */}
      {hasActivity && (
        <motion.div className="card" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-white font-bold uppercase tracking-wider text-sm" style={{ fontFamily: "'Rajdhani', sans-serif" }}>
              Activité des 7 derniers jours
            </h2>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-sm bg-qfl-gold" />
              <span className="text-white/40 text-xs">Parties</span>
            </div>
          </div>
          <BarChart data={activity} />
        </motion.div>
      )}

      {/* Alertes réelles */}
      {bannedUsers > 0 && (
        <motion.div
          className="card border-orange-500/30 bg-orange-500/5"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
        >
          <h3 className="text-orange-400 font-bold text-sm mb-2" style={{ fontFamily: "'Rajdhani', sans-serif" }}>
            Alertes
          </h3>
          <p className="text-white/50 text-sm">
            • {bannedUsers} compte{bannedUsers > 1 ? 's' : ''} banni{bannedUsers > 1 ? 's' : ''} — vérifier dans Utilisateurs
          </p>
        </motion.div>
      )}
    </div>
  );
}

export default AdminDashboard;


